import { supabase } from '@/integrations/supabase/client';
import { errorFromBody } from '@/lib/googleWorkspaceLogic';

export interface GoogleLabelSelection { gmail_label_id: string; label_name: string; project_id: string | null }
export interface GoogleStatus {
  connected: boolean;
  email?: string;
  status?: 'active' | 'needs_reauth';
  lastSyncedAt?: string | null;
  lastError?: string | null;
  labels: GoogleLabelSelection[];
}
export interface GmailLabel { id: string; name: string; type: string }
export interface LabelPick { id: string; name: string; projectId?: string | null }
export interface SyncCounts { fetched: number; removed: number; pruned: number; upserted: number; transcripts: number }
export type SyncResult = SyncCounts | { skipped: 'sync_in_progress' | 'not_active' };

// All requests go through the google-workspace edge function with the user's session;
// the client never sees or sends Google tokens.
async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('google-workspace', { body });
  if (error) {
    const res = (error as { context?: Response }).context;
    const detail = res && typeof res.json === 'function' ? await res.json().catch(() => null) : null;
    throw errorFromBody(detail, res?.status);
  }
  if (data && typeof data === 'object' && 'error' in data) throw errorFromBody(data);
  return data as T;
}

export const googleWorkspace = {
  status: () => call<GoogleStatus>({ action: 'status' }),
  authUrl: () => call<{ url: string }>({ action: 'auth_url' }),
  callback: (p: { code: string | null; state: string | null; error: string | null }) =>
    call<{ success: true }>({ action: 'callback', ...p }),
  listLabels: () => call<{ labels: GmailLabel[] }>({ action: 'list_labels' }),
  saveLabels: (labels: LabelPick[], purgeRemoved: boolean) =>
    call<{ ok: true; purged: number; pendingPurge?: boolean }>({ action: 'save_labels', labels, purgeRemoved }),
  syncNow: () => call<SyncResult>({ action: 'sync_now' }),
  reply: (emailId: string, body: string) => call<{ ok: true; messageId: string }>({ action: 'reply', emailId, body }),
  send: (to: string, subject: string, body: string) =>
    call<{ ok: true; messageId: string }>({ action: 'send', to, subject, body }),
  disconnect: (purge: boolean) => call<{ ok: true }>({ action: 'disconnect', purge }),
};
